(function () {
  'use strict';

  const SELECTOR = '.custom-variant-selector';
  const CARD_SELECTOR = '.variant-option-card';

  function parseJSON(rawValue) {
    if (!rawValue) return null;
    try {
      return JSON.parse(rawValue);
    } catch (error) {
      return null;
    }
  }

  function normalizeVariantId(value) {
    if (value === null || value === undefined) return '';
    const valueAsString = String(value);
    const numericMatch = valueAsString.match(/(\d+)(?!.*\d)/);
    return numericMatch ? numericMatch[1] : valueAsString;
  }

  function parseOptionIndexFromInputName(inputName) {
    if (!inputName) return null;
    const positionMatch = inputName.match(/-(\d+)$/);
    if (!positionMatch) return null;
    const position = Number(positionMatch[1]);
    if (Number.isNaN(position)) return null;
    return position - 1;
  }

  class CustomVariantSelector {
    constructor(root) {
      this.root = root;
      this.productInfo = root.closest('product-info');
      this.productId = root.dataset.productId ? String(root.dataset.productId) : '';
      this.sectionId = root.dataset.sectionId || '';
      this.cards = Array.from(root.querySelectorAll(CARD_SELECTOR));

      const parsedData = parseJSON(root.querySelector('.custom-variant-selector-data')?.textContent);
      if (!parsedData || !Array.isArray(parsedData.variants)) {
        return;
      }

      this.data = parsedData;
      this.variants = this.data.variants;
      this.sizeOptionIndex = Number(root.dataset.sizeOptionIndex || this.data.sizeOptionIndex);
      this.variantById = new Map();
      this.unsubscribeVariantChange = null;

      this.variants.forEach((variant) => {
        this.variantById.set(normalizeVariantId(variant.id), variant);
      });

      this.onCardClick = this.onCardClick.bind(this);
      this.onInputChange = this.onInputChange.bind(this);
      this.onVariantControlChange = this.onVariantControlChange.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.init();
    }

    init() {
      if (!this.cards.length || Number.isNaN(this.sizeOptionIndex)) return;

      this.root.addEventListener('click', this.onCardClick);
      this.root.addEventListener('change', this.onInputChange);

      const eventScope = this.productInfo || document;
      eventScope.addEventListener('change', this.onVariantControlChange);

      if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined' && PUB_SUB_EVENTS.variantChange) {
        this.unsubscribeVariantChange = subscribe(PUB_SUB_EVENTS.variantChange, this.onVariantChange);
      }

      this.bindCarouselControls();
      this.refreshCards();
    }

    onVariantControlChange(event) {
      const variantSelects = event.target.closest('variant-selects');
      if (!variantSelects) return;
      if (!this.isEventFromSameProduct(variantSelects)) return;

      window.requestAnimationFrame(() => this.refreshCards());
      window.setTimeout(() => this.refreshCards(), 450);
    }

    onVariantChange(payload) {
      if (!this.isVariantChangeForSameProduct(payload)) return;
      this.refreshCards(payload?.data?.variant);
      window.setTimeout(() => this.refreshCards(payload?.data?.variant), 50);
    }

    isMatchingSection(sectionId) {
      if (!this.sectionId) return true;
      return this.sectionId === sectionId;
    }

    isEventFromSameProduct(variantSelects) {
      const sourceProductInfo = variantSelects.closest('product-info');

      if (this.productInfo) {
        return sourceProductInfo === this.productInfo;
      }

      if (this.productId && sourceProductInfo?.dataset?.productId) {
        return String(sourceProductInfo.dataset.productId) === this.productId;
      }

      if (this.sectionId && sourceProductInfo?.dataset?.section) {
        return this.sectionId === sourceProductInfo.dataset.section;
      }

      return true;
    }

    isVariantChangeForSameProduct(payload) {
      const sectionId = payload?.data?.sectionId;
      const variantProductId = payload?.data?.variant?.product_id;

      if (this.productInfo && sectionId) {
        return this.isMatchingSection(sectionId);
      }

      if (this.productId && variantProductId) {
        return String(variantProductId) === this.productId;
      }

      return true;
    }

    getVariantSelects() {
      if (this.productInfo) {
        return this.productInfo.querySelector('variant-selects');
      }

      if (this.productId) {
        const productInfoByProductId = document.querySelector(`product-info[data-product-id="${this.productId}"]`);
        if (productInfoByProductId) {
          return productInfoByProductId.querySelector('variant-selects');
        }
      }

      if (this.sectionId) {
        const scopedContainer =
          document.querySelector(`#MainProduct-${this.sectionId}`) ||
          document.querySelector(`[data-section="${this.sectionId}"]`);
        if (scopedContainer) {
          return scopedContainer.querySelector('variant-selects');
        }
      }

      return document.querySelector('variant-selects');
    }

    getOptionIndexFromInput(input, variantSelects = null) {
      const parsedIndex = parseOptionIndexFromInputName(input?.name);
      if (parsedIndex !== null && parsedIndex >= 0) {
        return parsedIndex;
      }

      const fieldset = input?.closest('fieldset');
      if (!fieldset) return null;

      const scope = variantSelects || this.getVariantSelects();
      if (!scope) return null;

      const fieldsets = Array.from(scope.querySelectorAll('fieldset'));
      const fallbackIndex = fieldsets.indexOf(fieldset);
      return fallbackIndex >= 0 ? fallbackIndex : null;
    }

    onCardClick(event) {
      const card = event.target.closest(CARD_SELECTOR);
      if (!card || !this.root.contains(card)) return;

      const isAvailable = card.dataset.variantAvailable === 'true';
      if (!isAvailable) {
        event.preventDefault();
        return;
      }

      const sizeValue = card.dataset.sizeValue;
      if (!sizeValue) return;
      this.applySizeSelection(sizeValue, card);
    }

    onInputChange(event) {
      const input = event.target;
      if (!input.classList.contains('variant-radio-input')) return;
      if (!input.checked || input.disabled) return;

      const card = input.closest(CARD_SELECTOR);
      if (!card || card.dataset.variantAvailable !== 'true') return;

      const sizeValue = card.dataset.sizeValue;
      if (!sizeValue) return;
      this.applySizeSelection(sizeValue, card);
    }

    applySizeSelection(sizeValue, card) {
      const variantSelects = this.getVariantSelects();
      let didDispatch = false;

      if (variantSelects) {
        const matchingRadio = Array.from(variantSelects.querySelectorAll('input[type="radio"]')).find((input) => {
          const optionIndex = this.getOptionIndexFromInput(input, variantSelects);
          return optionIndex === this.sizeOptionIndex && input.value === sizeValue;
        });

        if (matchingRadio) {
          if (!matchingRadio.checked) {
            matchingRadio.checked = true;
            matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
          }
          didDispatch = true;
        } else {
          const matchingSelectById = variantSelects.querySelector(`#Option-${this.sectionId}-${this.sizeOptionIndex}`);
          const matchingSelectByIndex = Array.from(variantSelects.querySelectorAll('select'))[this.sizeOptionIndex];
          const matchingSelect = matchingSelectById || matchingSelectByIndex;

          if (matchingSelect && matchingSelect.value !== sizeValue) {
            matchingSelect.value = sizeValue;
            matchingSelect.dispatchEvent(new Event('change', { bubbles: true }));
            didDispatch = true;
          }
        }
      }

      if (didDispatch) return;

      const fallbackVariantId = normalizeVariantId(card.dataset.variantId);
      if (!fallbackVariantId) return;
      this.updateVariantInputs(fallbackVariantId);
      this.refreshCards();
    }

    updateVariantInputs(variantId) {
      const scope = this.productInfo || document;
      const productForms = scope.querySelectorAll(
        `#product-form-${this.sectionId}, #product-form-installment-${this.sectionId}, product-form`
      );

      productForms.forEach((productForm) => {
        const input = productForm.querySelector('input[name="id"]');
        if (!input) return;
        input.value = variantId;
        input.dispatchEvent(new Event('change', { bubbles: true }));
      });
    }

    getCurrentVariant(publishedVariant = null) {
      const publishedVariantId = normalizeVariantId(publishedVariant?.id);
      if (publishedVariantId && this.variantById.has(publishedVariantId)) {
        return this.variantById.get(publishedVariantId);
      }

      const scope = this.productInfo || document;
      const hiddenInput = scope.querySelector(
        `#product-form-${this.sectionId} input[name="id"], #product-form-installment-${this.sectionId} input[name="id"], product-form input[name="id"]`
      );
      const hiddenVariantId = normalizeVariantId(hiddenInput?.value);
      if (hiddenVariantId && this.variantById.has(hiddenVariantId)) {
        return this.variantById.get(hiddenVariantId);
      }

      const urlVariantId = normalizeVariantId(new URLSearchParams(window.location.search).get('variant'));
      if (urlVariantId && this.variantById.has(urlVariantId)) {
        return this.variantById.get(urlVariantId);
      }

      const variantSelects = this.getVariantSelects();
      const selectedVariant = parseJSON(variantSelects?.querySelector('[data-selected-variant]')?.textContent);
      const selectedVariantId = normalizeVariantId(selectedVariant?.id);
      if (selectedVariantId && this.variantById.has(selectedVariantId)) {
        return this.variantById.get(selectedVariantId);
      }

      return null;
    }

    getSelectedOptionValues() {
      const selectedValues = [];
      const variantSelects = this.getVariantSelects();

      if (variantSelects) {
        Array.from(variantSelects.querySelectorAll('select')).forEach((select, index) => {
          const idIndexMatch = select.id ? select.id.match(/-(\d+)$/) : null;
          const optionIndex = idIndexMatch ? Number(idIndexMatch[1]) : index;
          selectedValues[optionIndex] = select.value;
        });

        variantSelects.querySelectorAll('fieldset input[type="radio"]:checked').forEach((input) => {
          const optionIndex = this.getOptionIndexFromInput(input, variantSelects);
          if (optionIndex === null || optionIndex < 0) return;
          selectedValues[optionIndex] = input.value;
        });

        const selectedVariant = parseJSON(variantSelects.querySelector('[data-selected-variant]')?.textContent);
        if (Array.isArray(selectedVariant?.options)) {
          selectedVariant.options.forEach((optionValue, index) => {
            if (!selectedValues[index]) selectedValues[index] = optionValue;
          });
        }
      }

      const currentVariant = this.getCurrentVariant();
      if (Array.isArray(currentVariant?.options)) {
        currentVariant.options.forEach((optionValue, index) => {
          if (!selectedValues[index]) selectedValues[index] = optionValue;
        });
      }

      return selectedValues;
    }

    findVariantForOptions(desiredOptions) {
      return (
        this.variants.find((variant) => {
          if (!Array.isArray(variant.options)) return false;

          return variant.options.every((optionValue, index) => {
            const desiredValue = desiredOptions[index];
            if (desiredValue === undefined || desiredValue === null || desiredValue === '') return true;
            return optionValue === desiredValue;
          });
        }) || null
      );
    }

    findFallbackVariantForSize(sizeValue) {
      return (
        this.variants.find((variant) => {
          return Array.isArray(variant.options) && variant.options[this.sizeOptionIndex] === sizeValue;
        }) || null
      );
    }

    refreshCards(publishedVariant = null) {
      const selectedOptions = this.getSelectedOptionValues();
      const currentVariant = this.getCurrentVariant(publishedVariant);
      const activeSizeValue = currentVariant?.options?.[this.sizeOptionIndex] || selectedOptions[this.sizeOptionIndex];

      this.cards.forEach((card) => {
        const sizeValue = card.dataset.sizeValue;
        if (!sizeValue) return;

        const desiredOptions = selectedOptions.slice();
        desiredOptions[this.sizeOptionIndex] = sizeValue;

        const variant = this.findVariantForOptions(desiredOptions);
        const fallbackVariant = variant || this.findFallbackVariantForSize(sizeValue);
        this.updateCardState(card, variant, fallbackVariant, activeSizeValue);
      });
    }

    updateCardState(card, variant, fallbackVariant, activeSizeValue) {
      const radioInput = card.querySelector('.variant-radio-input');
      const sizeValue = card.dataset.sizeValue;
      const hasVariant = Boolean(variant);
      const isAvailable = hasVariant && Boolean(variant.available);
      const shouldDisable = !isAvailable;

      card.dataset.variantExists = hasVariant ? 'true' : 'false';
      card.dataset.variantAvailable = isAvailable ? 'true' : 'false';
      card.dataset.variantId = hasVariant ? String(variant.id) : '';

      card.classList.toggle('sold-out', shouldDisable);
      card.classList.toggle('active', Boolean(activeSizeValue) && sizeValue === activeSizeValue && hasVariant);

      if (radioInput) {
        radioInput.disabled = shouldDisable;
        radioInput.value = hasVariant ? String(variant.id) : '';
        radioInput.checked = Boolean(activeSizeValue) && sizeValue === activeSizeValue && hasVariant;
      }

      this.updateCardPricing(card, fallbackVariant);
      this.updateStatusLabel(card, hasVariant, isAvailable);
    }

    updateCardPricing(card, variant) {
      if (!variant) return;

      const pricingContainer = card.querySelector('.variant-pricing');
      const priceElement = pricingContainer?.querySelector('.variant-price');
      if (!pricingContainer || !priceElement) return;

      const priceValue = Number(variant.price || 0);
      const compareAtValue = Number(variant.compareAtPrice || 0);
      const isOnSale = compareAtValue > priceValue;

      priceElement.textContent = `\u20B9 ${variant.priceText || ''}`;
      priceElement.classList.toggle('sale-price', isOnSale);

      let comparePriceElement = pricingContainer.querySelector('.variant-compare-price');
      if (isOnSale) {
        if (!comparePriceElement) {
          comparePriceElement = document.createElement('span');
          comparePriceElement.className = 'variant-compare-price';
          pricingContainer.appendChild(comparePriceElement);
        }
        comparePriceElement.textContent = variant.compareAtPriceText || '';
      } else if (comparePriceElement) {
        comparePriceElement.remove();
      }
    }

    updateStatusLabel(card, hasVariant, isAvailable) {
      let statusLabel = card.querySelector('.sold-out-label');

      if (hasVariant && isAvailable) {
        if (statusLabel) statusLabel.remove();
        return;
      }

      if (!statusLabel) {
        statusLabel = document.createElement('span');
        statusLabel.className = 'sold-out-label';
        card.querySelector('.variant-card-label')?.appendChild(statusLabel);
      }

      statusLabel.textContent = hasVariant ? 'Sold Out' : 'Unavailable';
    }

    bindCarouselControls() {
      const variantGrid = this.root.querySelector('.variant-options-grid');
      const prevBtn = this.root.querySelector('.prev-btn');
      const nextBtn = this.root.querySelector('.next-btn');
      const container = this.root.querySelector('.variant-options-container');
      const variantCount = container ? parseInt(container.dataset.variantCount, 10) : 0;

      if (!prevBtn || !nextBtn || !variantGrid || variantCount < 3) return;

      prevBtn.addEventListener('click', function () {
        const scrollAmount = variantGrid.offsetWidth * 0.45;
        variantGrid.scrollBy({
          left: -scrollAmount,
          behavior: 'smooth',
        });
      });

      nextBtn.addEventListener('click', function () {
        const scrollAmount = variantGrid.offsetWidth * 0.45;
        variantGrid.scrollBy({
          left: scrollAmount,
          behavior: 'smooth',
        });
      });
    }
  }

  function initSelector(root) {
    if (!root || root.dataset.customVariantSelectorInitialized === 'true') return;
    root.dataset.customVariantSelectorInitialized = 'true';
    new CustomVariantSelector(root);
  }

  function initAll(container) {
    if (!container?.querySelectorAll) return;
    container.querySelectorAll(SELECTOR).forEach(initSelector);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      initAll(document);
    });
  } else {
    initAll(document);
  }

  const mutationObserver = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      mutation.addedNodes.forEach((node) => {
        if (node.nodeType !== 1) return;
        if (node.matches && node.matches(SELECTOR)) {
          initSelector(node);
        }
        initAll(node);
      });
    });
  });

  mutationObserver.observe(document.documentElement, { childList: true, subtree: true });
})();
